import { AlertTriangle, Database } from "lucide-react";
import React from "react";

import { authModeLabel } from "@/auth/sso-messages";
import { extractErrorMessage } from "@/lib/api-errors";
import { effectiveBaseName, ssoOneCProblem, useOneCBases } from "@/lib/use-onec-bases";
import { cn } from "@/lib/utils";

/** Режим входа текущей сессии — плашкой рядом с логином в шапке. Видно всегда:
 *  от режима зависит, как портал ходит в 1С (пароль подключения или JWT SSO).
 *  В SSO — ещё значок, если доступ к 1С не настроен (подробности — в меню). */
export function AuthModeBadge({ mode }: { mode: string | undefined }): React.ReactElement {
  return (
    <span
      data-testid="auth-mode"
      className="flex shrink-0 items-center gap-0.5 rounded bg-background/70 px-1 text-[10px] font-semibold uppercase tracking-wide"
    >
      {authModeLabel(mode)}
      {mode === "sso" && <SsoProblemMark />}
    </span>
  );
}

function SsoProblemMark(): React.ReactElement | null {
  const { data, isError } = useOneCBases();
  if (!isError && (!data || ssoOneCProblem(data) === null)) return null;
  return (
    <AlertTriangle
      className="size-3 text-amber-600"
      aria-label="Не настроен доступ к 1С"
    />
  );
}

/** Блок меню аккаунта для SSO-сессии: действующая база 1С или причина, по
 *  которой 1С недоступна. Ошибку загрузки показываем текстом, не прячем. */
export function SsoOneCInfo(): React.ReactElement {
  const { data, isLoading, error } = useOneCBases();
  const box = "border-b px-3 py-2 text-xs";
  if (isLoading) return <div className={cn(box, "text-muted-foreground")}>База 1С: загрузка…</div>;
  if (error || !data) {
    return (
      <div className={cn(box, "text-destructive")}>
        Справочник баз 1С не загрузился: {extractErrorMessage(error, "нет ответа")}
      </div>
    );
  }
  const problem = ssoOneCProblem(data);
  if (problem) {
    return (
      <div className={cn(box, "space-y-0.5 bg-amber-50 text-amber-900")} role="alert">
        <div className="flex items-center gap-1 font-semibold">
          <AlertTriangle className="size-3.5 shrink-0" />
          Не настроен доступ к 1С
        </div>
        <div>{problem}</div>
      </div>
    );
  }
  return (
    <div className={cn(box, "flex items-center gap-1.5 text-muted-foreground")}>
      <Database className="size-3.5 shrink-0" />
      <span className="truncate">База 1С: {effectiveBaseName(data)}</span>
    </div>
  );
}
